import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2, Save, ExternalLink, GripVertical, Undo2, Redo2, Sparkles, Tag } from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, resolveStatus, EmptyState } from "../../components/common";
import { toast } from "sonner";

const FORMATS = [
  "number",            // en-IN K/L/Cr: 3,900,000 → 39 L
  "number_full",       // full with en-IN commas: 39,00,000
  "number_western",    // K/M/B: 3,900,000 → 3.9M
  "currency",          // ₹ + K/L/Cr
  "currency_full",     // ₹ + full
  "currency_western",  // ₹ + K/M/B
  "percentage",
  "text",
];
const AGGS = ["SUM", "COUNT", "AVG", "MAX", "MIN", "None"];
const FIELD_TYPES = ["text", "number", "currency", "date", "url", "dropdown"];
const ICONS = ["eye", "trending-up", "users", "file-check", "dollar-sign", "calendar", "shopping-bag", "bar-chart-3"];
const STATUS_COLORS = ["green", "amber", "red", "blue", "gray"];

export default function ConfiguratorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [config, setConfigRaw] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [tab, setTab] = useState("overview");
  const [history, setHistory] = useState([]);    // snapshots BEFORE each change
  const [future, setFuture] = useState([]);       // snapshots AFTER redo
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  // Wrap setConfig so every mutation pushes the previous state onto history (max 25).
  // Pure setter — side-effects happen outside React's strict-mode double-invoke.
  const setConfig = (next) => {
    if (config) {
      const snap = JSON.stringify(config);
      setHistory((h) => (h[h.length - 1] === snap ? h : [...h.slice(-24), snap]));
      setFuture([]);
    }
    setConfigRaw(typeof next === "function" ? next(config) : next);
  };

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFuture((f) => [JSON.stringify(config), ...f].slice(0, 25));
    setConfigRaw(JSON.parse(prev));
  };

  const redo = () => {
    if (!future.length) return;
    const nextSnap = future[0];
    setFuture((f) => f.slice(1));
    setHistory((h) => [...h, JSON.stringify(config)].slice(-25));
    setConfigRaw(JSON.parse(nextSnap));
  };

  const revertToLastSaved = () => {
    if (!savedSnapshot) return;
    if (!window.confirm("Discard all unsaved changes and revert to the last saved configuration?")) return;
    setHistory((h) => [...h, JSON.stringify(config)].slice(-25));
    setFuture([]);
    setConfigRaw(JSON.parse(savedSnapshot));
  };

  // Keyboard shortcuts: Cmd/Ctrl-Z, Cmd/Ctrl-Shift-Z
  useEffect(() => {
    const handler = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (meta && (e.key.toLowerCase() === "z" && e.shiftKey || e.key.toLowerCase() === "y")) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, future, config]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, cfg_, s] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/config`),
        api.get(`/campaigns/${id}/sheet-data`),
      ]);
      setCampaign(c.data.campaign);
      const cfg = cfg_.data.config;
      setConfigRaw(cfg);
      setSavedSnapshot(JSON.stringify(cfg));
      setHistory([]);
      setFuture([]);
      setSheet(s.data.sheet);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${id}/config`, {
        overview_config: config.overview_config,
        creators_config: config.creators_config,
        content_config: config.content_config,
        tracker_config: config.tracker_config,
        status_mappings: config.status_mappings || [],
      });
      setSavedSnapshot(JSON.stringify(config));
      // Re-fetch the sheet data so newly-added custom tabs (and renamed sheet
      // tab references) take effect immediately — without this, the columns
      // dropdown for `custom_<id>` source tabs stays empty until the next page
      // reload, and the Custom tabs panel never sees its own columns either.
      // `refresh=1` busts the in-process sheet cache too, so any sheet-name
      // typos get corrected on the next save.
      try {
        const s = await api.get(`/campaigns/${id}/sheet-data?refresh=1`);
        setSheet(s.data.sheet);
      } catch { /* non-fatal — config itself was saved successfully */ }
      toast.success("Configuration saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !config) {
    return <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  const sheetTabs = sheet?.tabs || {};
  const allColumns = (tabName) => sheetTabs[tabName]?.headers || [];

  return (
    <div data-testid="configurator-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin/campaigns" className="text-xs text-[var(--text-secondary)] inline-flex items-center gap-1 mb-2"><ArrowLeft size={12} /> All campaigns</Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Dashboard configurator</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{campaign.campaign_name} · {campaign.campaign_id}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={undo} disabled={!history.length} className="wl-btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title="Undo (Ctrl/Cmd+Z)" data-testid="config-undo"><Undo2 size={14} /> Undo{history.length ? ` (${history.length})` : ""}</button>
          <button onClick={redo} disabled={!future.length} className="wl-btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" title="Redo (Ctrl/Cmd+Shift+Z)" data-testid="config-redo"><Redo2 size={14} /> Redo{future.length ? ` (${future.length})` : ""}</button>
          <button onClick={revertToLastSaved} disabled={!savedSnapshot || JSON.stringify(config) === savedSnapshot} className="wl-btn-ghost disabled:opacity-40 disabled:cursor-not-allowed" title="Discard all unsaved changes" data-testid="config-revert">Revert</button>
          <Link to={`/campaigns/${id}/tracking?preview=1`} className="wl-btn-ghost inline-flex items-center gap-1.5"><ExternalLink size={14} /> Preview tracker</Link>
          <button onClick={save} className="wl-btn-primary inline-flex items-center gap-1.5" disabled={saving} data-testid="config-save"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      {sheet?.error && <div className="wl-card p-4 mb-4 text-sm text-[#991b1b]">Sheet error: {sheet.error}</div>}
      {!campaign.google_sheet_id && <div className="wl-card p-4 mb-4 text-sm">No Google Sheet connected. Some features won't work until you connect one in the campaign edit modal.</div>}

      {/* Tabs */}
      <div className="border-b border-[var(--border-default)] mb-6 flex gap-6 text-sm">
        {[
          { k: "overview", l: "Overview slots" },
          { k: "creators", l: "Creator columns" },
          { k: "content", l: "Content columns" },
          { k: "custom_tabs", l: "Custom tabs" },
          { k: "tracker", l: "Tracker fields" },
          { k: "labels", l: "Status labels" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`pb-3 border-b-2 transition-colors ${tab === t.k ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`} data-testid={`config-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewSection
          config={config.overview_config}
          setConfig={(c) => setConfig({ ...config, overview_config: c })}
          columnsForTab={allColumns}
          sheet={sheet}
          statusMappings={config.status_mappings || []}
          creatorsConfig={config.creators_config}
          contentConfig={config.content_config}
          customTabs={config.tracker_config?.custom_tabs || []}
        />
      )}
      {tab === "creators" && (
        <ColumnsSection
          config={config.creators_config}
          setConfig={(c, opts) => setConfig({ ...config, creators_config: c }, opts)}
          allColumns={allColumns("creators")}
          sheet={sheet}
          tabKey="creators"
          statusMappings={config.status_mappings || []}
          testidPrefix="creators"
        />
      )}
      {tab === "content" && (
        <ColumnsSection
          config={config.content_config}
          setConfig={(c, opts) => setConfig({ ...config, content_config: c }, opts)}
          allColumns={allColumns("content")}
          sheet={sheet}
          tabKey="content"
          statusMappings={config.status_mappings || []}
          testidPrefix="content"
        />
      )}
      {tab === "custom_tabs" && (
        <CustomTabsSection
          customTabs={config.tracker_config?.custom_tabs || []}
          setCustomTabs={(arr) => setConfig({ ...config, tracker_config: { ...(config.tracker_config || {}), custom_tabs: arr } })}
          sheetTabsAvailable={Object.keys(sheet?.tabs || {})}
          allColumnsForCustom={allColumns}
          sheet={sheet}
        />
      )}
      {tab === "tracker" && (
        <TrackerSection config={config.tracker_config} setConfig={(c) => setConfig({ ...config, tracker_config: c })} />
      )}
      {tab === "labels" && (
        <StatusLabelsSection
          mappings={config.status_mappings || []}
          setMappings={(m) => setConfig({ ...config, status_mappings: m })}
          sheet={sheet}
        />
      )}
    </div>
  );
}

// ─── Row filter picker (shared across Creators tab, Content tab, and Overview slots) ─
// Lets the admin pick ANY column in the tab and tick the values they want to
// keep. The historical "Status" / "Opt-in Status" heuristic is preserved as a
// soft suggestion (preselects that column if it exists), but is no longer a
// hard requirement — any column with discrete values can drive the filter.
// Empty selection means "show all" (default, back-compat).
function suggestStatusColumn(headers) {
  return headers.find((h) => /status|opt[\s\-_]?in/i.test(String(h))) || null;
}

function valuesForColumn({ sheet, tabKey, column, statusMappings }) {
  const tab = sheet?.tabs?.[tabKey];
  const rows = tab?._all_rows || tab?.rows || [];
  const fromRows = column
    ? [...new Set(rows.map((r) => r[column]).filter((v) => v !== null && v !== undefined && String(v).trim() !== ""))]
    : [];
  // Status mappings only make sense if the user is filtering on a status-like
  // column — include their `raw_value`s as candidate chips so admins can
  // pre-emptively tick statuses they expect even before sheet data lands.
  const looksStatusy = column && /status|opt[\s\-_]?in/i.test(String(column));
  const fromMappings = looksStatusy ? (statusMappings || []).map((m) => m.raw_value).filter(Boolean) : [];
  return [...new Set([...fromRows, ...fromMappings])];
}

function StatusFilterPicker({ value, onChange, sheet, tabKey, statusMappings, statusColumn, onStatusColumnChange, testidPrefix }) {
  const tab = sheet?.tabs?.[tabKey];
  const headers = tab?.headers || [];
  // The "effective" column: explicit saved column wins; otherwise auto-suggest
  // a status-like column for back-compat. If neither, picker stays in "pick
  // a column" empty state.
  const effectiveColumn = statusColumn && headers.includes(statusColumn)
    ? statusColumn
    : (statusColumn ? null : suggestStatusColumn(headers));
  const available = useMemo(
    () => valuesForColumn({ sheet, tabKey, column: effectiveColumn, statusMappings }),
    [sheet, tabKey, effectiveColumn, statusMappings]
  );
  const selected = Array.isArray(value) && value.length ? value : null;
  const toggle = (s) => {
    const current = new Set(selected || available);
    if (current.has(s)) current.delete(s); else current.add(s);
    const arr = [...current];
    onChange(arr.length === available.length ? [] : arr);
  };

  if (!headers.length) {
    return (
      <div className="text-xs text-[var(--text-secondary)] bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`${testidPrefix}-status-filter-empty`}>
        No columns available yet — connect a Google Sheet (or wait for it to load) and the column list will appear here.
      </div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-status-filter`}>
      {/* Column picker — admin chooses ANY column to filter by */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-[var(--text-secondary)]">Filter by column:</span>
        <select
          className="wl-input wl-input-sm w-auto"
          value={effectiveColumn || ""}
          onChange={(e) => onStatusColumnChange?.(e.target.value || null)}
          data-testid={`${testidPrefix}-status-column-select`}
        >
          <option value="">— no filter —</option>
          {headers.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className="text-[10px] text-[var(--text-secondary)] ml-auto">
          {!effectiveColumn ? "No filter active" :
            selected ? `${selected.length} of ${available.length} values kept` : `All ${available.length} values kept`}
        </span>
      </div>

      {!effectiveColumn && (
        <div className="text-xs text-[var(--text-secondary)] bg-[var(--bg-surface-warm)] border border-[var(--border-default)] rounded-lg p-3" data-testid={`${testidPrefix}-status-filter-no-column`}>
          Pick a column above to start filtering. Common choices: <span className="font-mono">Status</span>, <span className="font-mono">Opt-in Status</span>, <span className="font-mono">Platform</span>, <span className="font-mono">Tier</span> — anything with a small set of repeating values.
        </div>
      )}

      {effectiveColumn && !available.length && (
        <div className="text-xs text-[var(--text-secondary)] bg-amber-50 border border-amber-200 rounded-lg p-3">
          Column <span className="font-mono">{effectiveColumn}</span> has no values yet. Once the sheet has rows the unique values will appear here as chips to tick.
        </div>
      )}

      {effectiveColumn && available.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {available.map((s) => {
            const resolved = resolveStatus(s, statusMappings);
            const looksStatusy = /status|opt[\s\-_]?in/i.test(String(effectiveColumn));
            const label = looksStatusy && resolved?.label ? resolved.label : String(s);
            const isOn = !selected || selected.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggle(s)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${isOn ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-white text-[var(--text-secondary)] border-[var(--border-default)] hover:border-[var(--text-secondary)]"}`}
                data-testid={`${testidPrefix}-status-chip-${s}`}
                title={String(s) !== label ? String(s) : undefined}
              >
                {label}
              </button>
            );
          })}
          {selected && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs px-2.5 py-1 rounded-full border border-dashed border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              data-testid={`${testidPrefix}-status-clear`}
            >Show all</button>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Overview slot configurator ──────────────────────────────────────────────
function OverviewSection({ config, setConfig, columnsForTab, sheet, statusMappings, creatorsConfig, contentConfig, customTabs }) {
  const slots = config.slots || [];
  const updateSlot = (i, patch) => setConfig({ ...config, slots: slots.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  const addSlot = (row) => setConfig({ ...config, slots: [...slots, { id: `slot-${Date.now()}`, row, enabled: true, label: "New slot", tab: "creators", column: "", format: "number", aggregation: "SUM", icon: "bar-chart-3", subtext: [], viz: row >= 2 ? "none" : "none" }] });
  const removeSlot = (i) => setConfig({ ...config, slots: slots.filter((_, idx) => idx !== i) });

  // Build the Source Tab options. The three fixed tabs always come first; any
  // configured custom tabs are appended so admins can build Overview metrics
  // sourced from custom sheet tabs too. Custom tabs without a sheet name set
  // are omitted (they'd return no data anyway).
  const tabOptions = useMemo(() => {
    const base = [
      { value: "campaign", label: "campaign" },
      { value: "creators", label: "creators" },
      { value: "content",  label: "content"  },
    ];
    const customs = (customTabs || [])
      .filter((t) => (t.sheet_tab_name || "").trim() && t.id)
      .map((t) => ({ value: `custom_${t.id}`, label: (t.label || t.sheet_tab_name) + "  (custom)" }));
    return [...base, ...customs];
  }, [customTabs]);

  const rows = [1, 2, 3];
  return (
    <div className="space-y-6" data-testid="overview-config">
      <div className="text-sm text-[var(--text-secondary)]">Configure card slots for the Overview tab. Row 1 = hero (large) cards. Row 2 = secondary cards (with optional progress ring or stacked bar). Row 3 = optional.</div>
      {rows.map((row) => (
        <div key={row}>
          <div className="flex items-center justify-between mb-2">
            <div className="wl-label">Row {row}</div>
            <button onClick={() => addSlot(row)} className="wl-btn-ghost inline-flex items-center gap-1 text-xs" data-testid={`add-slot-row-${row}`}><Plus size={12} /> Add slot</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {slots.map((s, i) => s.row === row && (
              <div key={s.id || i} className="wl-card p-4" data-testid={`slot-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <Toggle value={s.enabled} onChange={(v) => updateSlot(i, { enabled: v })} label="Enabled" testid={`slot-${s.id}-enabled`} />
                  <button onClick={() => removeSlot(i)} className="wl-btn-destructive text-xs"><Trash2 size={12} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Card label"><input className="wl-input" value={s.label || ""} onChange={(e) => updateSlot(i, { label: e.target.value })} data-testid={`slot-${s.id}-label`} /></Field>
                  <Field label="Source tab"><select className="wl-input" value={s.tab || "creators"} onChange={(e) => updateSlot(i, { tab: e.target.value, column: "", comparison_column: null, progress_target_tab: null, progress_target_column: null })} data-testid={`slot-${s.id}-source-tab`}>{tabOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field>
                  <Field label="Column">
                    <select className="wl-input" value={s.column || ""} onChange={(e) => updateSlot(i, { column: e.target.value })} data-testid={`slot-${s.id}-column`}>
                      <option value="">—</option>
                      {columnsForTab(s.tab).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Aggregation"><select className="wl-input" value={s.aggregation || "SUM"} onChange={(e) => updateSlot(i, { aggregation: e.target.value })}>{AGGS.map(a => <option key={a} value={a}>{a}</option>)}</select></Field>
                  <Field label="Format"><select className="wl-input" value={s.format || "number"} onChange={(e) => updateSlot(i, { format: e.target.value })}>{FORMATS.map(f => <option key={f} value={f}>{f}</option>)}</select></Field>
                  <Field label="Icon"><select className="wl-input" value={s.icon || "bar-chart-3"} onChange={(e) => updateSlot(i, { icon: e.target.value })}>{ICONS.map(o => <option key={o} value={o}>{o}</option>)}</select></Field>
                  <Field label="Filter value (optional)" hint="aggregate WHERE column = value"><input className="wl-input" value={s.filter_value || ""} onChange={(e) => updateSlot(i, { filter_value: e.target.value })} /></Field>
                  <Field label="Comparison column (optional)" hint="renders delta % badge"><select className="wl-input" value={s.comparison_column || ""} onChange={(e) => updateSlot(i, { comparison_column: e.target.value || null })}><option value="">—</option>{columnsForTab(s.tab).map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
                  {s.row >= 2 && (
                    <Field label="Visualization"><select className="wl-input" value={s.viz || "none"} onChange={(e) => updateSlot(i, { viz: e.target.value })}><option value="none">None</option><option value="progress_ring">Progress Ring</option><option value="stacked_bar">Stacked Bar</option></select></Field>
                  )}
                  {s.viz === "progress_ring" && (
                    <>
                      <Field label="Denominator tab" hint="which sheet tab to read the denominator from">
                        <select className="wl-input" value={s.progress_target_tab || s.tab || "creators"} onChange={(e) => updateSlot(i, { progress_target_tab: e.target.value, progress_target_column: null })} data-testid={`slot-${s.id}-target-tab`}>
                          {tabOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Denominator column (optional)" hint="if set, % = numerator / aggregate(this column, no filter). Overrides static target.">
                        <select className="wl-input" value={s.progress_target_column || ""} onChange={(e) => updateSlot(i, { progress_target_column: e.target.value || null })} data-testid={`slot-${s.id}-target-col`}>
                          <option value="">— use static target —</option>
                          {columnsForTab(s.progress_target_tab || s.tab).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Denominator aggregation" hint={s.progress_target_column ? "SUM works when the cell holds the target number itself; COUNT counts rows." : "only used when a denominator column is set"}>
                        <select className="wl-input" value={s.progress_target_aggregation || ""} onChange={(e) => updateSlot(i, { progress_target_aggregation: e.target.value || null })} disabled={!s.progress_target_column} data-testid={`slot-${s.id}-target-agg`}>
                          <option value="">Same as numerator ({s.aggregation || "SUM"})</option>
                          {AGGS.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </Field>
                      <Field label="Static target (fallback)" hint="used only when no denominator column is selected">
                        <input className="wl-input" type="number" min="0" value={s.progress_target ?? ""} onChange={(e) => updateSlot(i, { progress_target: e.target.value === "" ? null : Number(e.target.value) })} disabled={!!s.progress_target_column} data-testid={`slot-${s.id}-target-static`} />
                      </Field>
                    </>
                  )}
                  <div className="col-span-2"><Field label="Sub-text (one per line, supports {value} {value_abbreviated} {row_count})"><textarea className="wl-input" rows={2} value={(s.subtext || []).join("\n")} onChange={(e) => updateSlot(i, { subtext: e.target.value.split("\n").filter(Boolean) })} /></Field></div>
                  {/* Per-slot row filter override. For Creators/Content, defaults to
                      "inherit" (reflects the tab-level filter). For custom tabs there's
                      no tab-level filter so only "all" + "custom" are offered. The
                      Campaign tab (single row) doesn't get a row filter. */}
                  {(s.tab === "creators" || s.tab === "content" || (typeof s.tab === "string" && s.tab.startsWith("custom_"))) && (() => {
                    const isCustom = typeof s.tab === "string" && s.tab.startsWith("custom_");
                    // Custom tabs have no tab-level filter to inherit, so coerce the
                    // saved mode and don't surface the "inherit" pill.
                    const mode = (isCustom && (s.status_filter_mode || "inherit") === "inherit")
                      ? "all"
                      : (s.status_filter_mode || "inherit");
                    const modeOptions = isCustom
                      ? [{ v: "all", l: "All rows" }, { v: "custom", l: "Custom for this slot" }]
                      : [{ v: "inherit", l: `Inherit ${s.tab} tab filter` },
                         { v: "all", l: "Ignore filter (count all)" },
                         { v: "custom", l: "Custom for this slot" }];
                    return (
                      <div className="col-span-2">
                        <Field
                          label="Row filter for this slot"
                          hint={isCustom
                            ? "Custom tabs don't have a tab-level filter — pick \"Custom\" to filter this slot by any column from the custom tab."
                            : `Default inherits the ${s.tab} tab filter, so e.g. excluding "Pending" posts also excludes them from this metric.`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {modeOptions.map((opt) => {
                              const active = mode === opt.v;
                              return (
                                <button
                                  key={opt.v}
                                  type="button"
                                  onClick={() => updateSlot(i, { status_filter_mode: opt.v })}
                                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-white text-[var(--text-secondary)] border-[var(--border-default)] hover:border-[var(--text-secondary)]"}`}
                                  data-testid={`slot-${s.id}-status-mode-${opt.v}`}
                                >
                                  {opt.l}
                                </button>
                              );
                            })}
                          </div>
                          {mode === "custom" && (
                            <div className="mt-3 p-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-warm)]">
                              <StatusFilterPicker
                                value={s.status_filter_custom || []}
                                onChange={(arr) => updateSlot(i, { status_filter_custom: arr })}
                                sheet={sheet}
                                tabKey={s.tab}
                                statusMappings={statusMappings}
                                statusColumn={s.status_filter_column || (s.tab === "creators" ? creatorsConfig?.status_column : s.tab === "content" ? contentConfig?.status_column : null)}
                                onStatusColumnChange={(col) => updateSlot(i, { status_filter_column: col, status_filter_custom: [] })}
                                testidPrefix={`slot-${s.id}`}
                              />
                            </div>
                          )}
                          {!isCustom && mode === "inherit" && (() => {
                            const tabCfg = s.tab === "creators" ? creatorsConfig : contentConfig;
                            const col = tabCfg?.status_column;
                            const allowed = tabCfg?.allowed_statuses || [];
                            if (!col && !allowed.length) return null;
                            return (
                              <div className="mt-2 text-[11px] text-[var(--text-secondary)]">
                                Currently inheriting{col ? <> filter on <span className="font-mono">{col}</span></> : null}
                                {allowed.length ? <>: {allowed.map((a) => resolveStatus(a, statusMappings)?.label || a).join(", ")}</> : null}
                              </div>
                            );
                          })()}
                        </Field>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Custom tabs configurator ────────────────────────────────────────────────
// Admin can create extra tabular tabs (beyond the fixed Overview/Creators/Content),
// each pointing at any sheet tab in the campaign's connected Google Sheet. Stored
// at `tracker_config.custom_tabs`. Up to ~5 tabs is fine; the brand tracker shows
// them after Creator Summary / Content Detail in the configured order.
function CustomTabsSection({ customTabs, setCustomTabs, sheetTabsAvailable, allColumnsForCustom, sheet }) {
  const add = () => {
    const id = `c-${Date.now().toString(36)}`;
    setCustomTabs([...customTabs, {
      id,
      label: `Custom tab ${customTabs.length + 1}`,
      sheet_tab_name: "",
      tab_visible: true,
      columns: [],
      order: (customTabs.at(-1)?.order || 0) + 1,
    }]);
  };
  const upd = (idx, patch) => setCustomTabs(customTabs.map((t, i) => i === idx ? { ...t, ...patch } : t));
  const remove = (idx) => setCustomTabs(customTabs.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...customTabs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((t, i) => (t.order = i + 1));
    setCustomTabs(next);
  };

  return (
    <div className="space-y-6" data-testid="custom-tabs-config">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-[var(--text-secondary)] max-w-2xl">Add extra tabs that read any sheet in this campaign's Google Sheet. Each one renders as a table just like Creator Summary or Content Detail.</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">Tip: type the sheet tab name (case-sensitive), save once, then come back to pick which columns to show — the column list is populated from the fetched sheet.</p>
        </div>
        <button onClick={add} className="wl-btn-primary inline-flex items-center gap-1.5 text-sm shrink-0" data-testid="add-custom-tab"><Plus size={14} /> Add custom tab</button>
      </div>

      {customTabs.length === 0 && (
        <EmptyState title="No custom tabs yet" subtitle="Useful for things like Insights, Recommendations, Posts overview, etc." />
      )}

      {customTabs.map((t, idx) => {
        // Columns picker reads from sheet.tabs.custom_<id>.headers AFTER the
        // first save (when the backend refetches with the new tab_map). To
        // avoid the awkward "save → refresh → see columns" loop, we also fall
        // back to ANY already-fetched tab whose sheet-name matches the user's
        // input — most often when a custom tab is re-pointed at one of the
        // built-in tabs (creators / content), but also works across multiple
        // custom tabs that share a sheet name.
        let cols = allColumnsForCustom(`custom_${t.id}`) || [];
        let colsPreview = false;
        if (cols.length === 0 && t.sheet_tab_name && sheet?.tabs) {
          const target = String(t.sheet_tab_name).trim().toLowerCase();
          for (const [key, tab] of Object.entries(sheet.tabs)) {
            // direct-key match (custom-tab key is `custom_<id>`, built-ins are creators/content/campaign)
            if (key.toLowerCase() === target && tab?.headers?.length) { cols = tab.headers; colsPreview = true; break; }
          }
        }
        return (
          <div key={t.id} className="wl-card-solid p-5 space-y-4" data-testid={`custom-tab-${t.id}`}>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tab label" hint="shown to the brand in the tracker">
                <input className="wl-input" value={t.label} onChange={(e) => upd(idx, { label: e.target.value })} data-testid={`custom-tab-label-${t.id}`} />
              </Field>
              <Field label="Sheet tab name" hint="case-sensitive — must match the tab name in Google Sheets exactly">
                <input className="wl-input" value={t.sheet_tab_name} onChange={(e) => upd(idx, { sheet_tab_name: e.target.value })} placeholder="e.g. insights, recommendations" data-testid={`custom-tab-source-${t.id}`} />
              </Field>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-dashed border-[var(--border-dashed)]">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">Show this tab</span>
                <Toggle value={t.tab_visible !== false} onChange={(v) => upd(idx, { tab_visible: v })} testid={`custom-tab-visible-${t.id}`} />
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="wl-btn-ghost text-xs disabled:opacity-30" data-testid={`custom-tab-up-${t.id}`}><ChevronUp size={12} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === customTabs.length - 1} className="wl-btn-ghost text-xs disabled:opacity-30" data-testid={`custom-tab-down-${t.id}`}><ChevronDown size={12} /></button>
                <button onClick={() => remove(idx)} className="wl-btn-ghost text-xs text-[var(--brand)] inline-flex items-center gap-1" data-testid={`custom-tab-remove-${t.id}`}><Trash2 size={12} /> Remove</button>
              </div>
            </div>

            <div>
              <div className="wl-label mb-2 flex items-center gap-2">
                Columns
                {colsPreview && (
                  <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700" title="Showing columns from the matching sheet tab. Save to lock in this configuration.">
                    preview · save to lock in
                  </span>
                )}
              </div>
              {cols.length === 0 ? (
                <div className="text-xs text-[var(--text-secondary)] italic px-3 py-3 rounded bg-[var(--bg-surface-warm)] border border-dashed border-[var(--border-dashed)]">
                  {t.sheet_tab_name
                    ? `Save this configuration once. After saving, refresh — columns from the "${t.sheet_tab_name}" tab will appear here.`
                    : "Enter a sheet tab name above first."}
                </div>
              ) : (
                <CustomTabColumnsEditor
                  columns={t.columns || []}
                  setColumns={(arr) => upd(idx, { columns: arr })}
                  availableColumns={cols}
                  testidPrefix={`custom-${t.id}`}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Compact columns editor for a custom tab. Same shape as ColumnsSection's table but
// auto-syncs with the sheet headers: when a new column appears in the sheet, it's
// auto-added (visible=true); when one disappears it's flagged "missing in sheet".
function CustomTabColumnsEditor({ columns, setColumns, availableColumns, testidPrefix }) {
  // Auto-sync: ensure every header in the sheet has a row in `columns`.
  React.useEffect(() => {
    const present = new Set(columns.map((c) => c.column));
    const additions = availableColumns
      .filter((h) => !present.has(h))
      .map((h, i) => ({ column: h, display_name: h, format: "text", align: "left", visible: true, order: columns.length + i + 1 }));
    if (additions.length) setColumns([...columns, ...additions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableColumns.join("|")]);

  const upd = (idx, patch) => setColumns(columns.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const move = (idx, dir) => {
    const next = [...columns];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((c, i) => (c.order = i + 1));
    setColumns(next);
  };

  const sorted = columns.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="wl-card-solid overflow-hidden">
      <table className="wl-table">
        <thead><tr><th></th><th>Column (sheet)</th><th>Display name</th><th>Format</th><th>Align</th><th>Visible</th><th></th></tr></thead>
        <tbody>
          {sorted.map((c, i) => {
            const removed = !availableColumns.includes(c.column);
            return (
              <tr key={c.column} data-testid={`${testidPrefix}-col-${c.column}`}>
                <td><GripVertical size={14} className="text-[var(--text-secondary)]" /></td>
                <td className="font-medium">{c.column} {removed && <span className="ml-2 wl-pill wl-pill-expired">missing in sheet</span>}</td>
                <td><input className="wl-input" value={c.display_name} onChange={(e) => upd(i, { display_name: e.target.value })} /></td>
                <td><select className="wl-input" value={c.format} onChange={(e) => upd(i, { format: e.target.value })}>{FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}</select></td>
                <td><select className="wl-input" value={c.align} onChange={(e) => upd(i, { align: e.target.value })}><option value="left">left</option><option value="right">right</option><option value="center">center</option></select></td>
                <td><Toggle value={c.visible} onChange={(v) => upd(i, { visible: v })} testid={`${testidPrefix}-vis-${c.column}`} /></td>
                <td className="text-right pr-3">
                  <button onClick={() => move(i, -1)} className="wl-btn-ghost text-xs"><ChevronUp size={12} /></button>
                  <button onClick={() => move(i, 1)} className="wl-btn-ghost text-xs"><ChevronDown size={12} /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Generic columns section (used by Creators + Content tabs) ───────────────
function ColumnsSection({ config, setConfig, allColumns, sheet, tabKey, statusMappings, testidPrefix }) {
  // Sync columns from sheet (auto add new, mark removed) — skip history on auto-sync
  useEffect(() => {
    const existingIds = new Set((config.columns || []).map((c) => c.column));
    const newCols = allColumns.filter((c) => !existingIds.has(c)).map((c, i) => ({
      column: c, display_name: c, visible: false, format: "text", align: "left", order: 1000 + i,
    }));
    if (newCols.length) setConfig({ ...config, columns: [...(config.columns || []), ...newCols] }, { skipHistory: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns.join(",")]);

  const cols = (config.columns || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const upd = (idx, patch) => {
    const next = [...cols];
    next[idx] = { ...next[idx], ...patch };
    setConfig({ ...config, columns: next });
  };
  const move = (idx, dir) => {
    const next = [...cols];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((c, i) => (c.order = i));
    setConfig({ ...config, columns: next });
  };

  return (
    <div className="space-y-4">
      {/* Tab visibility toggle — when off, the brand tracker hides this tab entirely
          (no empty "—" rows). Per-campaign so each campaign can hide just the tabs
          that don't apply. Stored on the same config object so no schema change. */}
      <div className="wl-card-solid p-4 flex items-center gap-3 justify-between" data-testid={`${testidPrefix}-tab-visibility`}>
        <div>
          <div className="font-medium text-sm">Show this tab to the brand</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">Off = the brand tracker hides "{testidPrefix === "creators" ? "Creator Summary" : "Content Detail"}" entirely. Useful when this sheet tab isn't relevant for the campaign.</div>
        </div>
        <Toggle
          value={config.tab_visible !== false}
          onChange={(v) => setConfig({ ...config, tab_visible: v })}
          testid={`${testidPrefix}-tab-visible-toggle`}
        />
      </div>

      {/* Row filter — drops rows whose value in the chosen column isn't in the
          allow-list. Admin picks ANY column (defaults to a status-like one if
          present). Default (no column / empty list) means "show all" — identical
          to legacy behaviour. Also drives the default for Overview slots that
          source from this tab. */}
      <div className="wl-card-solid p-4" data-testid={`${testidPrefix}-status-filter-card`}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-medium text-sm">Row filter</div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
              Pick any column (e.g. <span className="font-mono">Status</span>, <span className="font-mono">Platform</span>, <span className="font-mono">Tier</span>) and tick the values to keep. {tabKey === "creators" ? "Useful for hiding opted-out creators or showing only one platform." : "Useful for hiding pending posts or showing only one content type."} Default (no column picked) keeps every row.
            </div>
          </div>
        </div>
        <StatusFilterPicker
          value={config.allowed_statuses || []}
          onChange={(arr) => setConfig({ ...config, allowed_statuses: arr })}
          sheet={sheet}
          tabKey={tabKey}
          statusMappings={statusMappings}
          statusColumn={config.status_column}
          onStatusColumnChange={(col) => setConfig({ ...config, status_column: col, allowed_statuses: [] })}
          testidPrefix={testidPrefix}
        />
      </div>
      <div className="wl-card-solid overflow-hidden" data-testid={`${testidPrefix}-columns`}>
        <table className="wl-table">
          <thead><tr><th></th><th>Column (sheet)</th><th>Display name</th><th>Format</th><th>Align</th><th>Visible</th><th></th></tr></thead>
          <tbody>
            {cols.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-[var(--text-secondary)]">No columns. Connect a Google Sheet to auto-populate.</td></tr>}
            {cols.map((c, i) => {
              const removed = !allColumns.includes(c.column);
              return (
                <tr key={c.column} data-testid={`${testidPrefix}-col-${c.column}`}>
                  <td><GripVertical size={14} className="text-[var(--text-secondary)] cursor-move" /></td>
                  <td className="font-medium">{c.column} {removed && <span className="ml-2 wl-pill wl-pill-expired">missing in sheet</span>}</td>
                  <td><input className="wl-input" value={c.display_name} onChange={(e) => upd(i, { display_name: e.target.value })} /></td>
                  <td><select className="wl-input" value={c.format} onChange={(e) => upd(i, { format: e.target.value })}>{FORMATS.map(f => <option key={f} value={f}>{f}</option>)}</select></td>
                  <td><select className="wl-input" value={c.align} onChange={(e) => upd(i, { align: e.target.value })}><option value="left">left</option><option value="right">right</option><option value="center">center</option></select></td>
                  <td><Toggle value={c.visible} onChange={(v) => upd(i, { visible: v })} testid={`${testidPrefix}-vis-${c.column}`} /></td>
                  <td className="text-right pr-3">
                    <button onClick={() => move(i, -1)} className="wl-btn-ghost text-xs"><ChevronUp size={12} /></button>
                    <button onClick={() => move(i, 1)} className="wl-btn-ghost text-xs"><ChevronDown size={12} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrackerSection({ config, setConfig }) {
  const fields = (config.fields || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const add = () => setConfig({ ...config, fields: [...fields, { id: `f-${Date.now()}`, name: "New field", type: "text", visible_to_brand: true, show_in_card: true, order: fields.length + 1 }] });
  const upd = (idx, patch) => {
    const next = [...fields];
    next[idx] = { ...next[idx], ...patch };
    setConfig({ ...config, fields: next });
  };
  const remove = (idx) => setConfig({ ...config, fields: fields.filter((_, i) => i !== idx) });
  const move = (idx, dir) => {
    const next = [...fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((c, i) => (c.order = i + 1));
    setConfig({ ...config, fields: next });
  };

  return (
    <div className="space-y-4" data-testid="tracker-config">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">Tracker fields appear on Brand Admin cards and the campaign create/edit modal. No field is compulsory.</p>
        <button onClick={add} className="wl-btn-primary inline-flex items-center gap-1.5 text-xs" data-testid="add-tracker-field"><Plus size={12} /> Add field</button>
      </div>
      <div className="wl-card-solid overflow-hidden">
        <table className="wl-table">
          <thead><tr><th></th><th>Name</th><th>Type</th><th>Options (dropdown)</th><th>Visible to brand</th><th>Show on card</th><th></th></tr></thead>
          <tbody>
            {fields.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-sm text-[var(--text-secondary)]">No tracker fields yet.</td></tr>}
            {fields.map((f, i) => (
              <tr key={f.id} data-testid={`tracker-field-${f.id}`}>
                <td><GripVertical size={14} className="text-[var(--text-secondary)]" /></td>
                <td><input className="wl-input" value={f.name} onChange={(e) => upd(i, { name: e.target.value })} /></td>
                <td><select className="wl-input" value={f.type} onChange={(e) => upd(i, { type: e.target.value })}>{FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></td>
                <td>{f.type === "dropdown" ? <input className="wl-input" placeholder="Comma separated" value={(f.options || []).join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /> : <span className="text-xs text-[var(--text-secondary)]">—</span>}</td>
                <td><Toggle value={f.visible_to_brand} onChange={(v) => upd(i, { visible_to_brand: v })} /></td>
                <td><Toggle value={f.show_in_card} onChange={(v) => upd(i, { show_in_card: v })} /></td>
                <td className="text-right pr-3">
                  <button onClick={() => move(i, -1)} className="wl-btn-ghost text-xs"><ChevronUp size={12} /></button>
                  <button onClick={() => move(i, 1)} className="wl-btn-ghost text-xs"><ChevronDown size={12} /></button>
                  <button onClick={() => remove(i)} className="wl-btn-destructive text-xs"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Toggle({ value, onChange, label, testid }) {
  return (
    <div className="inline-flex items-center gap-2">
      <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)}
        className={`relative w-9 h-5 rounded-full transition-colors ${value ? "bg-[var(--brand)]" : "bg-gray-300"}`} data-testid={testid}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${value ? "translate-x-4" : ""}`} />
      </button>
      {label && <span className="text-xs text-[var(--text-secondary)]">{label}</span>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return <label className="block"><div className="wl-label mb-1">{label}</div>{children}{hint && <div className="text-[11px] text-[var(--text-secondary)] mt-1">{hint}</div>}</label>;
}

const COLOR_PREVIEW = {
  green: { bg: "#dcfce7", text: "#166534", dot: "#22c55e" },
  amber: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  red:   { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
  blue:  { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  gray:  { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
};

function PreviewPill({ label, color }) {
  const c = COLOR_PREVIEW[color] || COLOR_PREVIEW.gray;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: c.bg, color: c.text }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
      {label || "—"}
    </span>
  );
}

function StatusLabelsSection({ mappings, setMappings, sheet }) {
  // Discover all distinct raw values across "status" / "opt-in" columns in creators + content tabs.
  // Skip the campaign tab (summary-only) and skip pure-numeric values that match status patterns by accident.
  const discovered = React.useMemo(() => {
    const set = new Set();
    const tabs = sheet?.tabs || {};
    ["creators", "content"].forEach((tabKey) => {
      const t = tabs[tabKey];
      if (!t) return;
      const cols = (t.headers || []).filter((h) => /status|opt[\s\-_]?in/i.test(h));
      (t.rows || []).forEach((r) => cols.forEach((c) => {
        const v = r[c];
        if (v === undefined || v === null) return;
        const s = String(v).trim();
        if (!s) return;
        if (/^-?[\d,.]+%?$/.test(s)) return; // skip pure numbers
        set.add(s);
      }));
    });
    return [...set].sort();
  }, [sheet]);

  // Stable client-side id per row so React can preserve focus when sorting / removing.
  const withId = (m) => m._cid ? m : { ...m, _cid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`) };

  const known = new Set(mappings.map((m) => String(m.raw || "").trim().toUpperCase()));
  const unmapped = discovered.filter((d) => !known.has(d.toUpperCase()));

  const upd = (idx, patch) => {
    const next = [...mappings];
    next[idx] = { ...next[idx], ...patch };
    setMappings(next);
  };
  const remove = (idx) => setMappings(mappings.filter((_, i) => i !== idx));
  const addRow = (raw = "") => setMappings([...mappings, withId({ raw, label: raw ? resolveStatus(raw)?.label || "" : "", color: raw ? resolveStatus(raw)?.color || "gray" : "gray" })]);

  const seedAll = () => {
    if (!unmapped.length) return;
    const additions = unmapped.map((raw) => {
      const r = resolveStatus(raw) || {};
      return withId({ raw, label: r.label || raw, color: r.color || "gray" });
    });
    setMappings([...mappings, ...additions]);
    toast.success(`Added ${additions.length} mapping${additions.length === 1 ? "" : "s"} from your sheet`);
  };

  return (
    <div className="space-y-4" data-testid="status-labels-config">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Tag size={16} className="text-[var(--brand)]" /> Status labels</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            Map raw values from your Google Sheet (e.g. <code className="text-[12px] bg-[var(--bg-tint)] px-1 py-0.5 rounded">OPT_OUT_BY_CREATOR</code>) to friendly display labels and colours. These will appear on the tracker dashboards in place of the raw text.
          </p>
        </div>
        <button onClick={seedAll} disabled={!unmapped.length} className="wl-btn-secondary inline-flex items-center gap-1.5 text-xs whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" data-testid="status-auto-seed">
          <Sparkles size={12} /> Auto-fill from sheet{unmapped.length ? ` (${unmapped.length})` : ""}
        </button>
      </div>

      <div className="wl-card-solid overflow-hidden">
        <table className="wl-table">
          <thead><tr><th>Sheet value (raw)</th><th>Display label</th><th>Colour</th><th>Preview</th><th></th></tr></thead>
          <tbody>
            {mappings.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-sm text-[var(--text-secondary)]">
                No custom mappings yet. Built-in defaults will be used (e.g. <code>ACKNOWLEDGED_YES → Opted In</code>). Click <span className="font-medium">Auto-fill from sheet</span> to override and customise.
              </td></tr>
            )}
            {mappings.map((m, i) => (
              <tr key={m._cid || `idx-${i}`} data-testid={`status-mapping-${i}`}>
                <td><input className="wl-input font-mono text-[13px]" value={m.raw || ""} onChange={(e) => upd(i, { raw: e.target.value })} placeholder="ACKNOWLEDGED_YES" data-testid={`status-raw-${i}`} /></td>
                <td><input className="wl-input" value={m.label || ""} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Opted In" data-testid={`status-label-${i}`} /></td>
                <td>
                  <select className="wl-input w-32" value={m.color || "gray"} onChange={(e) => upd(i, { color: e.target.value })} data-testid={`status-color-${i}`}>
                    {STATUS_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td><PreviewPill label={m.label} color={m.color} /></td>
                <td className="text-right pr-3">
                  <button onClick={() => remove(i)} className="wl-btn-destructive text-xs inline-flex items-center gap-1" data-testid={`status-remove-${i}`}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-[var(--border-default)] p-3 bg-[var(--bg-surface)] flex items-center justify-between">
          <div className="text-xs text-[var(--text-secondary)]">
            {discovered.length} raw value{discovered.length === 1 ? "" : "s"} found in your sheet · {unmapped.length} unmapped
          </div>
          <button onClick={() => addRow()} className="wl-btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="status-add-row"><Plus size={12} /> Add mapping</button>
        </div>
      </div>

      {unmapped.length > 0 && (
        <div className="wl-card p-4 text-xs text-[var(--text-secondary)]">
          <div className="font-medium text-[var(--text-primary)] mb-1.5">Unmapped values from your sheet</div>
          <div className="flex flex-wrap gap-1.5">
            {unmapped.map((u) => (
              <button key={u} onClick={() => addRow(u)} className="px-2 py-1 rounded bg-white border border-[var(--border-default)] hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors font-mono text-[11px]" data-testid={`unmapped-${u}`}>+ {u}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
